// As 6 áreas de navegação do app do Bubble (a página `index`), reproduzidas no hub
// (N11). É uma camada FIXA de navegação por cima do motor de acesso: os cards
// DENTRO de cada área continuam vindo das views (`cardsDoCurso`, `vw_vitrine_secoes`)
// — aqui só mora o mapa área → curso, porque as 6 áreas são conhecidas e não
// existe "área" no banco. Os slugs são os reais de `supabase/seed.sql`.
//
// Fase 1 (esta): a inicial mostra as 6 áreas e cada uma com acesso leva ao destino
// que JÁ existe (a página de curso, a Faculdade, a seção do Contrate). As páginas
// próprias de área (`/pos-graduacao` com os 7 cards, o "Header Produtos", a imagem
// dos cards) vêm nas fases seguintes.

export type AreaId = "escola" | "pos-graduacao" | "pericia" | "mba" | "aprovacao" | "contrate";

export type Area = {
  id: AreaId;
  nome: string;
  descricao: string;
  /** Slugs de curso do hub (seed.sql) que pertencem a esta área. Vazio = área de serviço (Contrate). */
  cursos: string[];
};

export type Pos = { slug: string; curto: string; nome: string };

// As 7 pós-graduações, na ordem do app do Bubble (página `pos-graduacao`). O rótulo
// `curto` é o do Bubble; o `nome` completo é o do seed. A `pos-auditoria` entrou no
// catálogo depois do seed (000192).
export const POS_GRADUACAO: Pos[] = [
  { slug: "pos-rt", curto: "Reforma Tributária", nome: "Pós-graduação em Reforma Tributária e Prática Fiscal" },
  { slug: "pos-dp", curto: "Departamento Pessoal", nome: "Pós-graduação em Departamento Pessoal e Legislação Trabalhista" },
  { slug: "pos-cgt", curto: "Contabilidade, Gestão e Tributação", nome: "Pós-graduação em Contabilidade, Gestão e Tributação" },
  { slug: "pos-casp", curto: "Setor Público", nome: "Pós-graduação em Contabilidade Aplicada ao Setor Público" },
  { slug: "pos-financas", curto: "Finanças e Controladoria", nome: "Pós-graduação em Finanças e Controladoria" },
  { slug: "pos-rotinas-simples", curto: "Rotinas do Simples", nome: "Pós-graduação em Rotinas do Simples Nacional" },
  { slug: "pos-auditoria", curto: "Auditoria e Compliance", nome: "Pós-graduação em Auditoria e Compliance" },
];

export const AREAS: Area[] = [
  {
    id: "escola",
    nome: "Escola Contábil",
    descricao: "Cursos, CFC de A a Z, ferramentas e comunidade.",
    cursos: ["escola-contabil", "escola-vitalicio"],
  },
  {
    id: "pos-graduacao",
    nome: "Pós-Graduações",
    descricao: "As pós-graduações da FBC, com secretaria e notas.",
    cursos: POS_GRADUACAO.map((p) => p.slug),
  },
  {
    id: "pericia",
    nome: "Perícia",
    descricao: "Formação Prática em Perícia Contábil e Financeira.",
    cursos: ["pericia"],
  },
  {
    id: "mba",
    nome: "MBA",
    descricao: "MBA em Empreendedorismo, Marketing e Gestão Contábil.",
    cursos: ["mba-gestao"],
  },
  {
    id: "aprovacao",
    nome: "Aprovação Contábil",
    descricao: "Preparação para concursos e exame de suficiência.",
    cursos: ["aprovacao-contabil"],
  },
  {
    id: "contrate",
    nome: "Contrate um Contador",
    descricao: "Ache um contador ou ofereça os seus serviços.",
    cursos: [],
  },
];

/** Todos os slugs de curso cobertos por alguma área — para achar curso órfão. */
export const SLUGS_COBERTOS: ReadonlySet<string> = new Set(AREAS.flatMap((a) => a.cursos));

/**
 * Para uma área e o acesso do aluno, diz se ele tem acesso e para onde a área
 * leva HOJE (destino que já existe). Área sem acesso não navega nesta fase — vira
 * um card "Sem acesso" (o Bubble mostra as 6 sempre). Nas próximas fases cada área
 * ganha página própria, e a sem acesso cai numa tela "você não tem acesso".
 */
export function destinoDaArea(
  area: Area,
  slugsComAcesso: ReadonlySet<string>,
  temContrate: boolean,
): { temAcesso: boolean; destino: string | null } {
  // O Contrate tem página própria desde 29/10, como no Bubble (antes, uma seção
  // da inicial).
  if (area.id === "contrate") {
    return { temAcesso: temContrate, destino: temContrate ? "/contrate-um-contador" : null };
  }
  const dono = area.cursos.find((slug) => slugsComAcesso.has(slug));
  if (!dono) return { temAcesso: false, destino: null };
  // A Pós reúne as 7 pós na Faculdade; MBA, Perícia e as livres levam ao curso.
  if (area.id === "pos-graduacao") return { temAcesso: true, destino: "/pos-graduacao" };
  return { temAcesso: true, destino: `/curso/${dono}` };
}
